import React from "react";
import { useLang } from "../context/LangContext";
import { Zap, Focus, Radio, Bed, Droplet, Hand, Sun } from "lucide-react";

const STIMULI = [
  {
    n: "01",
    icon: Zap,
    title: { fr: "Ondes de choc radiales", en: "Radial shockwaves", it: "Onde d'urto radiali", ru: "Радиальные ударные волны" },
    body: {
      fr: "Des impulsions mécaniques diffusées sur une zone relativement large.",
      en: "Mechanical pulses diffused over a relatively broad area.",
      it: "Impulsi meccanici diffusi su un'area relativamente ampia.",
      ru: "Механические импульсы, распределённые по относительно широкой зоне.",
    },
  },
  {
    n: "02",
    icon: Focus,
    title: { fr: "Ondes de choc focales", en: "Focused shockwaves", it: "Onde d'urto focali", ru: "Фокусированные ударные волны" },
    body: {
      fr: "Des impulsions mécaniques concentrées sur une zone plus précise.",
      en: "Mechanical pulses concentrated on a more precise area.",
      it: "Impulsi meccanici concentrati su un'area più precisa.",
      ru: "Механические импульсы, сфокусированные на более точной зоне.",
    },
  },
  {
    n: "03",
    icon: Radio,
    title: { fr: "Magnetolite", en: "Magnetolite", it: "Magnetolite", ru: "Магнитолит" },
    body: {
      fr: "Des stimulations par champs électromagnétiques pulsés.",
      en: "Stimulation via pulsed electromagnetic fields.",
      it: "Stimolazioni tramite campi elettromagnetici pulsati.",
      ru: "Стимуляция импульсными электромагнитными полями.",
    },
  },
  {
    n: "04",
    icon: Bed,
    title: { fr: "Lit plasma", en: "Plasma bed", it: "Lettino plasma", ru: "Плазменное ложе" },
    body: {
      fr: "Un temps de séance en position allongée sur le lit plasma.",
      en: "A session phase lying down on the plasma bed.",
      it: "Una fase della sessione distesi sul lettino plasma.",
      ru: "Часть сеанса в положении лёжа на плазменном ложе.",
    },
  },
  {
    n: "05",
    icon: Droplet,
    title: { fr: "Eau hydrogénée", en: "Hydrogenated water", it: "Acqua idrogenata", ru: "Водородная вода" },
    body: {
      fr: "De l'eau enrichie en hydrogène moléculaire, proposée au cours du parcours.",
      en: "Water enriched with molecular hydrogen, offered during the session.",
      it: "Acqua arricchita di idrogeno molecolare, offerta durante il percorso.",
      ru: "Вода, обогащённая молекулярным водородом, предлагаемая в ходе сеанса.",
    },
  },
  {
    n: "06",
    icon: Hand,
    title: { fr: "Massage énergétique", en: "Energetic massage", it: "Massaggio energetico", ru: "Энергетический массаж" },
    body: {
      fr: "Une approche manuelle centrée sur la détente et le ressenti corporel.",
      en: "A manual approach centred on relaxation and body awareness.",
      it: "Un approccio manuale centrato sul rilassamento e sulla percezione corporea.",
      ru: "Мануальный подход, направленный на расслабление и телесное восприятие.",
    },
  },
  {
    n: "07",
    icon: Sun,
    title: { fr: "Luminothérapie", en: "Light therapy", it: "Luce terapia", ru: "Светотерапия" },
    body: {
      fr: "Une exposition à une source lumineuse, selon le programme choisi.",
      en: "Exposure to a light source, according to the chosen program.",
      it: "Un'esposizione a una fonte luminosa, secondo il programma scelto.",
      ru: "Воздействие источника света в соответствии с выбранной программой.",
    },
  },
];

const HEADER = {
  fr: {
    eyebrow: "PROTOCOLE EXCLUSIF RIVIERA",
    title: "Notre thérapie : 7 stimulations dans un même parcours.",
    subtitle: "Leur utilisation, leur ordre et leurs réglages sont adaptés à votre objectif, après évaluation de votre situation et des éventuelles contre-indications.",
    footnote: "En complément, Exomobil propose des essais d'exosquelettes d'assistance à la marche, sur demande.",
  },
  en: {
    eyebrow: "EXCLUSIVE RIVIERA PROTOCOL",
    title: "Our therapy: 7 stimulations in a single journey.",
    subtitle: "Their use, order and settings are tailored to your objective, after evaluation of your situation and any contraindications.",
    footnote: "Additionally, Exomobil offers trials of walk-assist exoskeletons, on request.",
  },
  it: {
    eyebrow: "PROTOCOLLO ESCLUSIVO RIVIERA",
    title: "La nostra terapia: 7 stimolazioni in un unico percorso.",
    subtitle: "Il loro utilizzo, il loro ordine e le loro impostazioni sono adattati al vostro obiettivo, dopo valutazione della vostra situazione e di eventuali controindicazioni.",
    footnote: "In aggiunta, Exomobil propone prove di esoscheletri di assistenza alla marcia, su richiesta.",
  },
  ru: {
    eyebrow: "ЭКСКЛЮЗИВНЫЙ ПРОТОКОЛ RIVIERA",
    title: "Наша терапия: 7 стимуляций в одном сеансе.",
    subtitle: "Их использование, порядок и настройки адаптируются под вашу цель после оценки вашего состояния и возможных противопоказаний.",
    footnote: "Дополнительно Exomobil предлагает пробные сеансы экзоскелетов для помощи при ходьбе, по запросу.",
  },
};

export default function ProtocolExplained() {
  const { lang } = useLang();
  const h = HEADER[lang] || HEADER.en;

  return (
    <section className="container-luxe py-24 md:py-32" data-testid="protocol-explained">
      <div className="max-w-3xl">
        <div className="overline mb-4">{h.eyebrow}</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[var(--ink)] leading-[1.05]">
          {h.title}
        </h2>
        <p className="mt-5 text-[var(--text-secondary)] font-body leading-relaxed">
          {h.subtitle}
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {STIMULI.map((s) => {
          const Icon = s.icon;
          return (
            <article key={s.n} className="beam-card p-7 md:p-8" data-testid={`stimuli-${s.n}`}>
              <div className="flex items-center gap-3">
                <Icon size={20} className="text-[var(--gold)]" />
                <span className="overline">{s.n}</span>
              </div>
              <h3 className="font-display text-xl md:text-2xl text-[var(--ink)] mt-6 font-light leading-tight">
                {s.title[lang] || s.title.fr}
              </h3>
              <p className="mt-4 text-[14px] text-[var(--text-secondary)] leading-relaxed font-body">
                {s.body[lang] || s.body.fr}
              </p>
            </article>
          );
        })}
      </div>

      <p className="mt-10 text-[13px] text-[var(--text-secondary)] italic font-body max-w-3xl">
        {h.footnote}
      </p>
    </section>
  );
}
